import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Paginator } from 'primereact/paginator';
import { Toast } from 'primereact/toast';
import { Checkbox } from 'primereact/checkbox';
import agent from '../../../agent';
import SearchSelect from '../../CalculatorSplit/SearchSelect';

const MILE_TYPES = [
    { label: 'Первая миля', value: 'FIRST' },
    { label: 'Внутригород', value: 'INNER_CITY' },
    { label: 'Магистраль', value: 'MIDDLE' },
    { label: 'Последняя миля', value: 'LAST' },
];

const MILE_TYPE_LABELS = {
    FIRST: 'Первая миля',
    INNER_CITY: 'Внутригород',
    MIDDLE: 'Магистраль',
    LAST: 'Последняя миля',
};

const ACTIVE_OPTIONS = [
    { label: 'Все', value: null },
    { label: 'Да', value: true },
    { label: 'Нет', value: false },
];

const EMPTY_FORM = {
    mileType: '',
    katoFrom: '',
    katoTo: '',
    tariffZone: null,
    daysCnt: null,
    minWeightPrice: null,
    overweightPrice: null,
    isActive: true,
};

const EMPTY_FILTERS = {
    mileType: null,
    katoFrom: '',
    katoTo: '',
    tariffZone: null,
    daysCnt: null,
    isActive: null,
};

const formatPrice = (value) => {
    if (value == null) return '';
    return Number(value).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₸';
};

const MarketTariffs = () => {
    const toast = useRef(null);
    const fileInputRef = useRef(null);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [totalElements, setTotalElements] = useState(0);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [activeFilters, setActiveFilters] = useState(null);

    const [showFormDialog, setShowFormDialog] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [importMileType, setImportMileType] = useState('');
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const [katoFromState, setKatoFromState] = useState({ kato: '' });
    const [katoToState, setKatoToState] = useState({ kato: '' });
    const [fromOptions, setFromOptions] = useState([]);
    const [toOptions, setToOptions] = useState([]);

    const [formKatoFromState, setFormKatoFromState] = useState({ kato: '' });
    const [formKatoToState, setFormKatoToState] = useState({ kato: '' });
    const [formFromOptions, setFormFromOptions] = useState([]);
    const [formToOptions, setFormToOptions] = useState([]);

    const showMessage = useCallback((severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 5000 });
    }, []);

    const loadPage = useCallback(async (p = page, ps = pageSize, af = activeFilters) => {
        setLoading(true);
        try {
            let response;
            if (af) {
                const cleanFilters = Object.fromEntries(
                    Object.entries(af).filter(([, v]) => v !== null && v !== '' && v !== undefined)
                );
                response = await agent.MarketTariffs.search(cleanFilters, p, ps);
            } else {
                response = await agent.MarketTariffs.getPage(p, ps);
            }
            setData(response.content || []);
            setTotalElements(response.totalElements || 0);
        } catch {
            showMessage('error', 'Ошибка', 'Не удалось загрузить данные');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, activeFilters, showMessage]);

    useEffect(() => {
        loadPage(page, pageSize, activeFilters);
    }, [page, pageSize]);

    const onPageChange = (e) => {
        setPage(e.page);
        setPageSize(e.rows);
        loadPage(e.page, e.rows, activeFilters);
    };

    const handleSearch = () => {
        setPage(0);
        const merged = {
            ...filters,
            katoFrom: katoFromState.kato || '',
            katoTo: katoToState.kato || '',
        };
        setActiveFilters(merged);
        loadPage(0, pageSize, merged);
    };

    const handleReset = () => {
        setFilters(EMPTY_FILTERS);
        setKatoFromState({ kato: '' });
        setKatoToState({ kato: '' });
        setActiveFilters(null);
        setPage(0);
        loadPage(0, pageSize, null);
    };

    const validateForm = (f) => {
        const errors = {};
        if (!f.mileType) errors.mileType = true;
        if (!f.katoFrom || f.katoFrom.length !== 9) errors.katoFrom = true;
        if (!f.katoTo || f.katoTo.length !== 9) errors.katoTo = true;
        if (!f.minWeightPrice && f.minWeightPrice !== 0) errors.minWeightPrice = true;
        if (!f.overweightPrice && f.overweightPrice !== 0) errors.overweightPrice = true;
        return errors;
    };

    const openCreate = () => {
        setEditItem(null);
        setForm(EMPTY_FORM);
        setFormKatoFromState({ kato: '' });
        setFormKatoToState({ kato: '' });
        setFormErrors({});
        setShowFormDialog(true);
    };

    const openEdit = (row) => {
        setEditItem(row);
        setForm({
            mileType: row.mileType || '',
            katoFrom: row.katoFrom || '',
            katoTo: row.katoTo || '',
            tariffZone: row.tariffZone ?? null,
            daysCnt: row.daysCnt ?? null,
            minWeightPrice: row.minWeightPrice ?? null,
            overweightPrice: row.overweightPrice ?? null,
            isActive: row.isActive ?? true,
        });
        setFormKatoFromState({ kato: row.katoFrom || '', nameRu: row.katoFromName || '' });
        setFormKatoToState({ kato: row.katoTo || '', nameRu: row.katoToName || '' });
        setFormErrors({});
        setShowFormDialog(true);
    };

    const handleFormSave = async () => {
        const errors = validateForm(form);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }
        setFormLoading(true);
        try {
            if (editItem) {
                await agent.MarketTariffs.update(editItem.id, form);
                showMessage('success', 'Сохранено', 'Тариф обновлён');
            } else {
                await agent.MarketTariffs.create(form);
                showMessage('success', 'Создано', 'Тариф создан');
            }
            setShowFormDialog(false);
            loadPage(page, pageSize, activeFilters);
        } catch {
            showMessage('error', 'Ошибка', 'Не удалось сохранить тариф');
        } finally {
            setFormLoading(false);
        }
    };

    const openDelete = (row) => {
        setDeleteTarget(row);
        setShowDeleteDialog(true);
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            await agent.MarketTariffs.remove(deleteTarget.id);
            showMessage('success', 'Удалено', `Тариф #${deleteTarget.id} удалён`);
            setShowDeleteDialog(false);
            setDeleteTarget(null);
            loadPage(page, pageSize, activeFilters);
        } catch {
            showMessage('error', 'Ошибка', 'Не удалось удалить тариф');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleImport = async () => {
        const file = fileInputRef.current?.files?.[0];
        if (!importMileType) { showMessage('warn', 'Внимание', 'Выберите тип мили'); return; }
        if (!file) { showMessage('warn', 'Внимание', 'Выберите CSV файл'); return; }
        setImportLoading(true);
        setImportResult(null);
        try {
            const result = await agent.MarketTariffs.importCsv(importMileType, file);
            setImportResult(result);
            showMessage('success', 'Импорт', `Создано: ${result.created}, дублей: ${result.duplicates}, ошибок: ${result.errors?.length || 0}`);
            loadPage(page, pageSize, activeFilters);
        } catch {
            showMessage('error', 'Ошибка', 'Не удалось импортировать файл');
        } finally {
            setImportLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const onFormChangeFrom = (value) => {
        agent.Kato.search(value).then(response => {
            setFormFromOptions(Array.isArray(response) ? response : (response?.content || []));
        });
    };

    const onFormChangeTo = (value) => {
        agent.Kato.search(value).then(response => {
            setFormToOptions(Array.isArray(response) ? response : (response?.content || []));
        });
    };

    const onChangeFrom = (value) => {
        agent.Kato.search(value).then(response => {
            setFromOptions(Array.isArray(response) ? response : (response?.content || []));
        });
    };

    const onChangeTo = (value) => {
        agent.Kato.search(value).then(response => {
            setToOptions(Array.isArray(response) ? response : (response?.content || []));
        });
    };

    const isLastFilter = activeFilters?.mileType === 'LAST';
    const toColumnHeader = isLastFilter ? 'Нас. пункт куда' : 'Город куда';

    const actionsBody = (row) => (
        <div style={{ display: 'flex', gap: '4px' }}>
            <Button icon="pi pi-pencil" className="p-button-text p-button-sm" onClick={() => openEdit(row)} tooltip="Редактировать" tooltipOptions={{ position: 'top' }} />
            <Button icon="pi pi-trash" className="p-button-text p-button-sm p-button-danger" onClick={() => openDelete(row)} tooltip="Удалить" tooltipOptions={{ position: 'top' }} />
        </div>
    );

    const priceBody = (field) => (row) => formatPrice(row[field]);
    const activeBody = (row) => row.isActive ? 'Да' : 'Нет';
    const mileTypeBody = (row) => MILE_TYPE_LABELS[row.mileType] || row.mileType;

    const formField = (label, required, children, error) => (
        <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: required ? '600' : '400' }}>
                {label}{required && <span style={{ color: 'red' }}> *</span>}
            </label>
            {children}
            {error && <small style={{ color: 'red' }}>Обязательное поле</small>}
        </div>
    );

    return (
        <div style={{ padding: '1rem' }}>
            <Toast ref={toast} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h1 style={{ margin: 0 }}>Рыночная тарифная карта</h1>
                <Button label="Создать тариф" icon="pi pi-plus" onClick={openCreate} />
            </div>

            {/* Filters */}
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '6px', marginBottom: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Тип мили</label>
                        <Dropdown value={filters.mileType} options={[{ label: 'Все', value: null }, ...MILE_TYPES]}
                            onChange={(e) => setFilters({ ...filters, mileType: e.value })}
                            placeholder="Все" style={{ width: '100%' }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>КАТО откуда</label>
                        <SearchSelect placeholder="Город откуда" name="katoFrom"
                            state={katoFromState} setState={setKatoFromState}
                            options={fromOptions} optionValue="kato"
                            onChange={onChangeFrom} optionText="nameRu" />
                        {katoFromState.kato && <small style={{ color: '#666' }}>КАТО: {katoFromState.kato}</small>}
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>КАТО куда</label>
                        <SearchSelect placeholder="Город куда" name="katoTo"
                            state={katoToState} setState={setKatoToState}
                            options={toOptions} optionValue="kato"
                            onChange={onChangeTo} optionText="nameRu" />
                        {katoToState.kato && <small style={{ color: '#666' }}>КАТО: {katoToState.kato}</small>}
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Активен</label>
                        <Dropdown value={filters.isActive} options={ACTIVE_OPTIONS}
                            onChange={(e) => setFilters({ ...filters, isActive: e.value })}
                            style={{ width: '100%' }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Тарифная зона</label>
                        <InputNumber value={filters.tariffZone} onValueChange={(e) => setFilters({ ...filters, tariffZone: e.value })} style={{ width: '100%' }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Срок (дней)</label>
                        <InputNumber value={filters.daysCnt} onValueChange={(e) => setFilters({ ...filters, daysCnt: e.value })} style={{ width: '100%' }} />
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button label="Поиск" icon="pi pi-search" onClick={handleSearch} />
                    <Button label="Сбросить" icon="pi pi-times" className="p-button-outlined" onClick={handleReset} />
                </div>
            </div>

            {/* Import */}
            <div style={{ background: '#f0f4ff', padding: '1rem', borderRadius: '6px', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end' }}>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Тип мили для импорта</label>
                        <Dropdown value={importMileType} options={MILE_TYPES}
                            onChange={(e) => setImportMileType(e.value)}
                            placeholder="Выберите тип" style={{ width: '180px' }} />
                    </div>
                    <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>CSV файл</label>
                        <input ref={fileInputRef} type="file" accept=".csv" />
                    </div>
                    <Button label="Загрузить" icon="pi pi-upload" onClick={handleImport} loading={importLoading} />
                </div>
                {importResult && (
                    <div style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                        <span>Успешно создано: <b>{importResult.created}</b></span>
                        {' · '}
                        <span>Пропущено дублей: <b>{importResult.duplicates}</b></span>
                        {' · '}
                        <span>Ошибок: <b>{importResult.errors?.length || 0}</b></span>
                        {importResult.errors?.length > 0 && (
                            <ul style={{ color: 'red', marginTop: '4px' }}>
                                {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Table */}
            <DataTable value={data} loading={loading} dataKey="id" rowHover emptyMessage="Данные не найдены" scrollable>
                <Column field="id" header="ID" headerStyle={{ width: '70px' }} />
                <Column field="mileType" header="Тип мили" body={mileTypeBody} headerStyle={{ width: '140px' }} />
                <Column field="katoFromName" header="Город откуда" headerStyle={{ width: '160px' }} />
                <Column field="katoFrom" header="КАТО откуда" headerStyle={{ width: '130px' }} />
                <Column field="katoToName" header={toColumnHeader} headerStyle={{ width: '160px' }} />
                <Column field="katoTo" header="КАТО куда" headerStyle={{ width: '130px' }} />
                <Column field="tariffZone" header="Зона" headerStyle={{ width: '70px' }} />
                <Column field="daysCnt" header="Срок (дней)" headerStyle={{ width: '100px' }} />
                <Column field="minWeightPrice" header="Цена за мин. вес (2 кг)" body={priceBody('minWeightPrice')} headerStyle={{ width: '160px' }} />
                <Column field="overweightPrice" header="Цена за перевес (+1 кг)" body={priceBody('overweightPrice')} headerStyle={{ width: '160px' }} />
                <Column field="isActive" header="Активен" body={activeBody} headerStyle={{ width: '90px' }} />
                <Column header="" body={actionsBody} headerStyle={{ width: '90px' }} />
            </DataTable>
            <Paginator
                first={page * pageSize} rows={pageSize} totalRecords={totalElements}
                rowsPerPageOptions={[10, 25, 50, 100]} onPageChange={onPageChange}
                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Показано с {first} по {last} из {totalRecords}"
            />

            {/* Create/Edit Dialog */}
            <Dialog header={editItem ? `Редактирование тарифа #${editItem.id}` : 'Создание тарифа'}
                visible={showFormDialog} style={{ width: '480px' }}
                onHide={() => setShowFormDialog(false)}
                footer={
                    <div>
                        <Button label="Отмена" icon="pi pi-times" className="p-button-text" onClick={() => setShowFormDialog(false)} />
                        <Button label="Сохранить" icon="pi pi-check" onClick={handleFormSave} loading={formLoading} />
                    </div>
                }>
                {formField('Тип мили', true,
                    <Dropdown value={form.mileType} options={MILE_TYPES}
                        onChange={(e) => setForm({ ...form, mileType: e.value })}
                        placeholder="Выберите тип" style={{ width: '100%' }} />,
                    formErrors.mileType)}
                {formField('КАТО откуда', true,
                    <>
                        <SearchSelect placeholder="Откуда" name="formKatoFrom"
                            state={formKatoFromState}
                            setState={(val) => { setFormKatoFromState(val); setForm((f) => ({ ...f, katoFrom: val.kato || '' })); }}
                            options={formFromOptions} optionValue="kato"
                            onChange={onFormChangeFrom} optionText="nameRu" />
                        {formKatoFromState.kato && <small style={{ color: '#666' }}>КАТО: {formKatoFromState.kato}</small>}
                    </>,
                    formErrors.katoFrom)}
                {formField(form.mileType === 'LAST' ? 'КАТО куда (нас. пункт)' : 'КАТО куда', true,
                    <>
                        <SearchSelect placeholder="Куда" name="formKatoTo"
                            state={formKatoToState}
                            setState={(val) => { setFormKatoToState(val); setForm((f) => ({ ...f, katoTo: val.kato || '' })); }}
                            options={formToOptions} optionValue="kato"
                            onChange={onFormChangeTo} optionText="nameRu" />
                        {formKatoToState.kato && <small style={{ color: '#666' }}>КАТО: {formKatoToState.kato}</small>}
                    </>,
                    formErrors.katoTo)}
                {formField('Тарифная зона', false,
                    <InputNumber value={form.tariffZone} onValueChange={(e) => setForm({ ...form, tariffZone: e.value })} style={{ width: '100%' }} />)}
                {formField('Срок (дней)', false,
                    <InputNumber value={form.daysCnt} min={0} onValueChange={(e) => setForm({ ...form, daysCnt: e.value })} style={{ width: '100%' }} />)}
                {formField('Цена за мин. вес (2 кг)', true,
                    <InputNumber value={form.minWeightPrice} min={0} minFractionDigits={2} maxFractionDigits={2}
                        onValueChange={(e) => setForm({ ...form, minWeightPrice: e.value })}
                        style={{ width: '100%' }} />,
                    formErrors.minWeightPrice)}
                {formField('Цена за перевес (+1 кг)', true,
                    <InputNumber value={form.overweightPrice} min={0} minFractionDigits={2} maxFractionDigits={2}
                        onValueChange={(e) => setForm({ ...form, overweightPrice: e.value })}
                        style={{ width: '100%' }} />,
                    formErrors.overweightPrice)}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Checkbox checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.checked })} />
                    <label>Активен</label>
                </div>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog header="Подтверждение удаления" visible={showDeleteDialog} style={{ width: '380px' }}
                onHide={() => setShowDeleteDialog(false)}
                footer={
                    <div>
                        <Button label="Отмена" icon="pi pi-times" className="p-button-text" onClick={() => setShowDeleteDialog(false)} />
                        <Button label="Удалить" icon="pi pi-trash" className="p-button-danger" onClick={handleDelete} loading={deleteLoading} />
                    </div>
                }>
                <p>Вы уверены, что хотите удалить тариф #{deleteTarget?.id}?</p>
            </Dialog>
        </div>
    );
};

export default MarketTariffs;
